// Drop-in replacement for <select>: options in alphabetical order AND a search box in every dropdown.
//
// Every dropdown in the PMS uses this component (import Select from "components/common/Select"),
// so sorting and searching are done in one place, whatever the source of the list.
//
// Sorting rules:
// - Options with an empty value ("All departments", "Choose a manager", "No manager"...) and options marked
//   <option data-pin> stay at the top, in their original order.
// - The other options are sorted A to Z by their visible text, ignoring case and accents. Numbers are
//   compared as numbers, so "5 days" comes before "14 days" and "E2" before "E10".
// - Inside an <optgroup>, the options are sorted; the groups keep their order.
// - <Select keepOrder> turns sorting off for lists whose order has a meaning (months, workflow stages,
//   rating bands from best to worst).
//
// [NEW] Search (requirement 2 "every dropdown has a search"):
// - The closed control looks like the old <select>. Clicking it (or ArrowDown / Enter / Space, or simply typing)
//   opens a list with a search box on top. Typing filters the options (case and accents ignored, any part of the text).
// - Arrow keys move, Enter picks, Escape closes. Disabled options cannot be picked.
// - The list opens in a layer above dialogs, so it is never cut off by a modal or a scrolling card.
// - A hidden real <select> keeps the value, so `onChange={(e) => set(e.target.value)}`, `name`, `ref` and
//   form submission keep working exactly as before. `searchable={false}` falls back to the plain <select>.
import {
  Children, Fragment, cloneElement, forwardRef, isValidElement, useCallback, useEffect, useId, useImperativeHandle,
  useMemo, useRef, useState,
} from "react";
import { createPortal } from "react-dom";
import { FiSearch } from "react-icons/fi";

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

// Visible text of an <option>, e.g. <option>{m.name} ({m.id})</option> -> "Asha HR (E1001)"
function textOf(node) {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (isValidElement(node)) return textOf(node.props.children);
  return "";
}

// Expands fragments and arrays (from .map) into a flat list of <option>/<optgroup> elements.
function flatten(children) {
  const out = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    if (child.type === Fragment) out.push(...flatten(child.props.children));
    else out.push(child);
  });
  return out;
}

const isPlaceholder = (el) => el.type === "option" && (el.props.value === "" || Boolean(el.props["data-pin"]) || (el.props.value === undefined && textOf(el.props.children) === ""));

function sortOptions(elements) {
  const top = [];
  const options = [];
  const groups = [];
  elements.forEach((el) => {
    if (el.type === "optgroup") groups.push(el);
    else (isPlaceholder(el) ? top : options).push(el);
  });
  options.sort((a, b) => collator.compare(textOf(a.props.children).trim(), textOf(b.props.children).trim()));
  // groups keep their order; the options inside each group are sorted
  return [...top, ...options, ...groups].map((el, i) => {
    const keyed = el.key === null ? cloneElement(el, { key: `opt-${i}-${String(el.props.value ?? textOf(el.props.children))}` }) : el;
    if (keyed.type === "optgroup") {
      return cloneElement(keyed, {}, sortOptions(flatten(keyed.props.children)));
    }
    return keyed;
  });
}

// Flat list used by the search popup: [{ value, label, disabled, group }]
function toEntries(elements, group = "") {
  const out = [];
  elements.forEach((el) => {
    if (el.type === "optgroup") out.push(...toEntries(flatten(el.props.children), el.props.label || ""));
    else if (el.type === "option") {
      const label = textOf(el.props.children).trim();
      const value = el.props.value === undefined ? label : String(el.props.value);
      out.push({ value, label, disabled: Boolean(el.props.disabled), group });
    }
  });
  return out;
}

const fold = (s) => String(s).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const Select = forwardRef(function Select(
  { children, keepOrder = false, searchable = true, className, style, id, disabled, onChange, value, defaultValue, placeholder, title, ...rest },
  ref
) {
  const items = flatten(children);
  const ordered = keepOrder ? items.map((el, i) => (el.key === null ? cloneElement(el, { key: `opt-${i}` }) : el)) : sortOptions(items);

  if (!searchable) {
    return (
      <select ref={ref} id={id} className={className} style={style} disabled={disabled} onChange={onChange} value={value} defaultValue={defaultValue} title={title} {...rest}>
        {ordered}
      </select>
    );
  }
  return (
    <SearchableSelect
      ref={ref} ordered={ordered} className={className} style={style} id={id} disabled={disabled}
      onChange={onChange} value={value} defaultValue={defaultValue} placeholder={placeholder} title={title} rest={rest}
    />
  );
});

const SearchableSelect = forwardRef(function SearchableSelect(
  { ordered, className, style, id, disabled, onChange, value, defaultValue, placeholder, title, rest }, ref
) {
  const entries = useMemo(() => toEntries(ordered), [ordered]);
  const nativeRef = useRef(null);
  const buttonRef = useRef(null);
  const popRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();
  const autoId = useId();
  const buttonId = id || `ss-${autoId}`;
  useImperativeHandle(ref, () => nativeRef.current);

  const controlled = value !== undefined;
  const [inner, setInner] = useState(defaultValue !== undefined ? String(defaultValue) : entries[0]?.value ?? "");
  const current = controlled ? (value === null ? "" : String(value)) : inner;
  const selected = entries.find((e) => e.value === current);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState(null);

  const q = fold(query.trim());
  const shown = useMemo(
    () => (q ? entries.filter((e) => fold(e.label).includes(q) || fold(e.group).includes(q)) : entries),
    [entries, q]
  );

  const place = useCallback(() => {
    const b = buttonRef.current;
    if (!b) return;
    const r = b.getBoundingClientRect();
    const width = Math.max(r.width, 220);
    const below = window.innerHeight - r.bottom;
    const up = below < 280 && r.top > below;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
    setPos(up ? { left, width, bottom: window.innerHeight - r.top + 4 } : { left, width, top: r.bottom + 4 });
  }, []);

  const openList = (initial = "") => {
    if (disabled) return;
    setQuery(initial);
    const idx = Math.max(0, entries.findIndex((e) => e.value === current));
    setActive(initial ? 0 : idx);
    place();
    setOpen(true);
  };
  const close = (refocus = true) => {
    setOpen(false);
    setQuery("");
    if (refocus) buttonRef.current?.focus();
  };

  const pick = (entry) => {
    if (!entry || entry.disabled) return;
    if (!controlled) setInner(entry.value);
    const el = nativeRef.current;
    if (el) el.value = entry.value;
    if (onChange) {
      // Same shape as a native change event: e.target.value / e.target.name / e.target.id
      const target = el || { value: entry.value, name: rest.name, id: buttonId };
      onChange({ target, currentTarget: target, type: "change", preventDefault() {}, stopPropagation() {}, persist() {} });
    }
    close();
  };

  // keep the active row valid and visible
  useEffect(() => { if (active >= shown.length) setActive(Math.max(0, shown.length - 1)); }, [shown.length, active]);
  useEffect(() => {
    if (!open || !listRef.current) return;
    const row = listRef.current.querySelector(`[data-index="${active}"]`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  useEffect(() => { if (open) place(); }, [open, place]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (buttonRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      close(false);
    };
    const onScroll = (e) => { if (!popRef.current?.contains(e.target)) place(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, place]); // eslint-disable-line react-hooks/exhaustive-deps

  const move = (step) => {
    if (!shown.length) return;
    let i = active;
    for (let n = 0; n < shown.length; n += 1) {
      i = (i + step + shown.length) % shown.length;
      if (!shown[i].disabled) break;
    }
    setActive(i);
  };

  const onButtonKey = (e) => {
    if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) { e.preventDefault(); openList(); }
    else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); openList(e.key); }
  };
  const onSearchKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); move(1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); move(-1); }
    else if (e.key === "Enter") { e.preventDefault(); pick(shown[active]); }
    else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); }
    else if (e.key === "Tab") close(false);
  };

  const label = selected ? selected.label : placeholder || entries[0]?.label || "";
  const ariaLabel = rest["aria-label"];
  const ariaLabelledBy = rest["aria-labelledby"];
  const nativeRest = { ...rest };
  delete nativeRest["aria-label"];
  delete nativeRest["aria-labelledby"];
  delete nativeRest["aria-describedby"];
  delete nativeRest["aria-invalid"];

  let lastGroup = null;
  return (
    <span className="ss">
      {/* The real <select> keeps value, name and ref for forms and older code; it is hidden from users. */}
      <select
        ref={nativeRef}
        {...nativeRest}
        className="ss-native"
        value={current}
        onChange={() => {}}
        disabled={disabled}
        tabIndex={-1}
        aria-hidden="true"
      >
        {ordered}
      </select>
      <button
        ref={buttonRef}
        type="button"
        id={buttonId}
        className={`${className || "form-control"} ss-button ${open ? "is-open" : ""} ${rest["aria-invalid"] === true || rest["aria-invalid"] === "true" ? "has-error" : ""}`.trim()}
        style={{ "--ss-ch": Math.min(40, entries.reduce((m, e) => Math.max(m, e.label.length), 4)), ...style }}
        disabled={disabled}
        title={title || label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={rest["aria-describedby"]}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onButtonKey}
      >
        <span className={`ss-value ${selected && selected.value !== "" ? "" : "is-placeholder"}`}>{label || " "}</span>
      </button>
      {open && pos && createPortal(
        <div
          ref={popRef}
          className="ss-pop"
          style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom }}
          role="dialog"
          aria-label={ariaLabel ? `Choose ${ariaLabel}` : "Choose an option"}
        >
          <label className="ss-search">
            <FiSearch size={14} aria-hidden="true" />
            <input
              autoFocus
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActive(0); }}
              onKeyDown={onSearchKey}
              placeholder="Search..."
              aria-label="Search options"
              aria-controls={listId}
              aria-activedescendant={shown[active] ? `${listId}-${active}` : undefined}
            />
          </label>
          <ul id={listId} ref={listRef} className="ss-list" role="listbox">
            {shown.map((e, i) => {
              const header = e.group && e.group !== lastGroup ? e.group : null;
              lastGroup = e.group;
              return (
                <Fragment key={`${e.group}|${e.value}|${i}`}>
                  {header && <li className="ss-group" role="presentation">{header}</li>}
                  <li
                    id={`${listId}-${i}`}
                    data-index={i}
                    role="option"
                    aria-selected={e.value === current}
                    aria-disabled={e.disabled || undefined}
                    className={`ss-option ${i === active ? "is-active" : ""} ${e.value === current ? "is-selected" : ""} ${e.disabled ? "is-disabled" : ""}`}
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(ev) => ev.preventDefault()}
                    onClick={() => pick(e)}
                  >
                    {e.label || " "}
                  </li>
                </Fragment>
              );
            })}
            {!shown.length && <li className="ss-empty">Nothing matches "{query}".</li>}
          </ul>
        </div>,
        document.body
      )}
    </span>
  );
});

export default Select;
export { textOf as optionText, collator as alphaCollator };
